/**
 * Auth adapter — bridges the Emergent UI's simple shape ({email, password})
 * to DyntraStore NestJS which expects {channel, dest, password, deviceId} and
 * uses an opaque otpId across otp/send + otp/verify.
 */
import { api } from "@/lib/api";

const DEVICE_ID_KEY = "dyntra_device_id";
const OTP_ID_KEY = "dyntra_otp_id";
const TOKEN_KEY = "dyntra_token";
const REFRESH_KEY = "dyntra_refresh_token";

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function pickChannel(dest) {
  return /^\+?\d/.test(String(dest).trim()) ? "sms" : "email";
}

function persistSession(data) {
  const access = data?.accessToken || data?.access_token;
  const refresh = data?.refreshToken || data?.refresh_token;
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

function normalizeUser(u) {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    name: u.name || u.fullName || u.email?.split("@")[0] || "",
    role: u.role || "CUSTOMER",
    phone: u.phone || null,
  };
}

export async function me() {
  const { data } = await api.get("/me");
  return normalizeUser(data);
}

/** Email + password login. */
export async function login(email, password) {
  const { data } = await api.post("/auth/password/login", {
    channel: pickChannel(email),
    dest: email,
    password,
    deviceId: getDeviceId(),
  });
  persistSession(data);
  return { ...normalizeUser(data.user || data), access_token: data.accessToken };
}

/** Register with name + email + password. NestJS also needs a phone, so we
 * derive a placeholder if not supplied; the app can prompt for a real one
 * later during OTP verification. */
export async function register(name, email, password, phone) {
  const { data } = await api.post("/auth/register", {
    email,
    phone: phone || "+910000000000",
    password,
    name,
    deviceId: getDeviceId(),
  });
  persistSession(data);
  return { ...normalizeUser(data.user || data), access_token: data.accessToken };
}

export async function logout() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  try {
    if (refreshToken) await api.post("/auth/logout", { refreshToken });
  } catch {
    /* swallow — clear locally anyway */
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** POST /v1/auth/otp/send — remember the returned otpId for the subsequent verify. */
export async function sendOtp(dest) {
  const { data } = await api.post("/auth/otp/send", {
    channel: pickChannel(dest),
    dest,
  });
  if (data?.otpId) localStorage.setItem(OTP_ID_KEY, data.otpId);
  return {
    otpId: data?.otpId,
    mock: Boolean(data?.mock),
    mock_code: data?.mockCode || data?.mock_code,
  };
}

/** POST /v1/auth/otp/verify — pulls otpId from the previous send. */
export async function verifyOtp(_dest, code) {
  const otpId = localStorage.getItem(OTP_ID_KEY);
  if (!otpId) throw new Error("OTP session expired. Please request a new code.");
  const { data } = await api.post("/auth/otp/verify", {
    otpId,
    code,
    deviceId: getDeviceId(),
  });
  persistSession(data);
  localStorage.removeItem(OTP_ID_KEY);
  return data;
}
